import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.kiera.debug');
import { OnDestroy, OnInit } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
 standalone: false,
 selector: 'wiz-page-kiera-debug',
template: templateSource || '',
    styles: [``],
})
export class PageKieraDebugComponent implements OnInit, OnDestroy {
    // PXE Debug toggles
    public config: any = {
        no_shutdown: false,
        commissioning: false,
        deploying: false,
        bypass_ipmi: false,
        integration_dhcp: true,
        integration_nfs: true,
        integration_squid: false,
    };
    public configMeta: any = {};
    public runtimeSupported: boolean = false;
    public parity: any[] = [];

    // Celery / Background Tasks
    public celery: any = {
        active_count: 0,
        reserved_count: 0,
        scheduled_count: 0,
        active_tasks: [],
        reserved_tasks: [],
    };

    // Diagnostics
    public diagnostics: any = {
        db: { status: 'unknown', message: 'Loading...' },
        pxe_services: [],
        system: {},
        pxe_data: [],
        runtime_flags: { flags: {}, active_files: [] },
        timestamp: '',
    };
    private langSub?: Subscription;

    constructor(@Inject( Service) public service: Service,@Inject( TranslateService)  private translate: TranslateService) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow("/access");
        await this.service.auth.loadPermissions();
        this.langSub = this.translate.onLangChange.subscribe(async () => {
            await this.load();
        });
        await this.load();
        await this.service.render();
    }

    public ngOnDestroy() {
        if (this.langSub) this.langSub.unsubscribe();
    }

    public async load() {
        const { code, data } = await wiz.call("load");
        if (code === 200) {
            const payload = this.localizeData(data || {});
            this.runtimeSupported = !!payload.runtime_supported;
            if (payload.config) this.config = { ...this.config, ...payload.config };
            if (payload.config_meta) this.configMeta = payload.config_meta;
            if (payload.parity) this.parity = payload.parity;
            if (payload.celery) this.celery = payload.celery;
            if (payload.diagnostics) this.diagnostics = payload.diagnostics;
        }
        await this.service.render();
    }

    public get canUpdate(): boolean { return this.service.auth.can("debug", "update"); }

    public async save() {
        if (!this.canUpdate) return;
        const { code, data, message } = await wiz.call("save", { config: JSON.stringify(this.config) });
        if (code === 200) { await this.load(); alert(this.translate.instant("page.debug.saved")); }
        else alert(message || data?.message || this.translate.instant("page.debug.save_failed"));
    }

    public async getCeleryTasks() {
        const { code, data } = await wiz.call("celery_tasks");
        if (code === 200 && data) this.celery = data;
        await this.service.render();
    }

    public async purgeCeleryTasks() {
        if (!confirm("Are you sure you want to purge all Celery tasks?")) return;
        await wiz.call("celery_purge");
        await this.getCeleryTasks();
    }

    public async refreshDiagnostics() {
        await this.load();
    }

    public getServiceStatusColor(status: string): string {
        if (status === 'active') return 'text-emerald-600';
        if (status === 'inactive') return 'text-zinc-400';
        return 'text-red-500';
    }

    public getServiceStatusIcon(status: string): string {
        if (status === 'active') return 'bx bxs-check-circle';
        if (status === 'inactive') return 'bx bxs-minus-circle';
        return 'bx bxs-x-circle';
    }

    public parityBadgeClass(status: string): string {
        if (status === 'wiz-only') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
        if (status === 'retired') return 'bg-zinc-100 text-zinc-600 border-zinc-200';
        if (status === 'partial') return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    }

    public parityLabel(status: string): string {
        if (status === 'wiz-only') return 'WIZ only';
        if (status === 'retired') return 'Retired';
        if (status === 'partial') return 'Partial';
        return 'Matched';
    }

    private isEnglishLanguage() {
        return String(this.translate.currentLang || this.translate.getDefaultLang() || 'ko').toLowerCase().startsWith('en');
    }

    private localizeText(value: string) {
        if (!this.isEnglishLanguage()) return value;

        const replacements: Array<[string, string]> = [
            ['기존 KiERA는 Redis DebugEnv 플래그로 commissioning cloud-init/meta-data를 debug content-type으로 노출했습니다.', 'Legacy KiERA exposed commissioning cloud-init/meta-data with a debug content-type through the Redis DebugEnv flag.'],
            ['현재 WIZ는 commissioning debug 시 preinstalled OS에 /tmp/kiera-commissioning.sh만 내려주고 자동 실행은 멈춘 뒤, 현장에서 수동 실행으로 이어가게 합니다.', 'Current WIZ drops only /tmp/kiera-commissioning.sh onto the preinstalled OS during commissioning debug, stops automatic execution, and hands off to an on-site manual run.'],
            ['기존 KiERA는 deploying vendor-data/script를 debug content-type으로 바꾸는 전역 플래그였습니다.', 'Legacy KiERA used this as a global flag that switched deploying vendor-data/script to a debug content-type.'],
            ['현재 WIZ는 deploying debug 시 preinstalled OS에 /tmp/kiera-deploying.sh만 내려주고 자동 실행은 멈춘 뒤, 현장에서 수동 실행으로 이어가게 합니다.', 'Current WIZ drops only /tmp/kiera-deploying.sh onto the preinstalled OS during deploying debug, stops automatic execution, and hands off to an on-site manual run.'],
            ['기존 KiERA는 node_ipmi chassis task에서 power cycle을 건너뛰는 실제 runtime 플래그였습니다.', 'Legacy KiERA used this as a real runtime flag to skip the power cycle in the node_ipmi chassis task.'],
            ['현재 WIZ는 actions/relay PXE 경로에서 노드 전원이 이미 켜져 있으면 강제 power cycle을 건너뜁니다.', 'Current WIZ skips the forced power cycle on the actions/relay PXE path when the node is already powered on.'],
            ['기존 KiERA는 subnet signal에서 isc-dhcp-server 재시작 여부를 이 플래그로 제어했습니다.', 'Legacy KiERA controlled whether isc-dhcp-server was restarted from the subnet signal with this flag.'],
            ['현재 WIZ는 subnet DHCP apply/regenerate 시 이 플래그를 읽어 isc-dhcp-server 재시작 여부를 제어합니다.', 'Current WIZ reads this flag during subnet DHCP apply/regenerate to control whether isc-dhcp-server is restarted.'],
            ['기존 KiERA는 subnet signal과 RDMA 분기에서 exportfs/NFS 연동 여부를 이 플래그로 제어했습니다.', 'Legacy KiERA controlled exportfs/NFS integration in subnet signal and RDMA branches with this flag.'],
            ['현재 WIZ는 NFS exports apply 시 이 플래그를 읽어 exportfs -ra 실행 여부를 제어합니다.', 'Current WIZ reads this flag during NFS exports apply to control whether exportfs -ra is executed.'],
            ['기존 KiERA는 subnet signal에서 squid 재시작 여부를 제어했습니다.', 'Legacy KiERA controlled whether squid was restarted from the subnet signal.'],
            ['현재 WIZ는 DHCP apply/regenerate 시 이 플래그를 읽어 squid restart를 시도합니다.', 'Current WIZ reads this flag during DHCP apply/regenerate and attempts a squid restart.'],
            ['기존 KiERA debug 설정에는 없던 항목입니다.', 'This item did not exist in the legacy KiERA debug settings.'],
            ['현재 WIZ는 /tmp/kiera-debug-no-shutdown runtime 파일을 실제로 생성/삭제해 마지막 shutdown 또는 reboot를 건너뜁니다.', 'Current WIZ actually creates or removes the /tmp/kiera-debug-no-shutdown runtime file to skip the final shutdown or reboot.'],
            ['WIZ 추가 항목이며 실제 /tmp/kiera-debug-no-shutdown 파일을 제어하는 런타임 플래그입니다.', 'This is a WIZ-only addition and a runtime flag that controls the real /tmp/kiera-debug-no-shutdown file.'],
            ['deploy/commission 흐름의 마지막 poweroff 또는 reboot를 건너뛰게 해 현장 디버깅용 PE 상태를 유지합니다. 기존 KiERA의 debug setting에는 없던 WIZ 전용 확장입니다.', 'It skips the final poweroff or reboot in deploy/commission flows so the PE state remains available for on-site debugging. This is a WIZ-only extension that did not exist in legacy KiERA debug settings.'],
            ['commissioning debug 시 preinstalled OS에서 자동 실행을 멈추고 /tmp/kiera-commissioning.sh 수동 실행으로 이어갑니다.', 'During commissioning debug, it stops automatic execution on the preinstalled OS and continues with a manual /tmp/kiera-commissioning.sh run.'],
            ['기존 KiERA는 commissioning cloud-init 응답을 debug override로 유지해 자동 진행을 끊을 수 있었습니다. 현재 WIZ도 commissioning debug가 켜지면 script만 write_files로 내려주고 runcmd 자동 실행은 생략해, 사전설치 OS에서 멈춘 뒤 현장에서 bash /tmp/kiera-commissioning.sh 로 이어가게 합니다.', 'Legacy KiERA could stop automatic progress by keeping the commissioning cloud-init response in a debug override state. Current WIZ does the same operationally by dropping only the script through write_files and skipping automatic runcmd execution, so the preinstalled OS pauses and operators continue with bash /tmp/kiera-commissioning.sh on site.'],
            ['deploying debug 시 preinstalled OS에서 자동 실행을 멈추고 /tmp/kiera-deploying.sh 수동 실행으로 이어갑니다.', 'During deploying debug, it stops automatic execution on the preinstalled OS and continues with a manual /tmp/kiera-deploying.sh run.'],
            ['기존 KiERA는 deploy vendor-data/script 응답을 debug 모드로 바꿔 자동 진행을 끊는 전역 플래그였습니다. 현재 WIZ도 deploying debug가 켜지면 script만 write_files로 내려주고 runcmd 자동 실행은 생략해, 사전설치 OS에서 멈춘 뒤 현장에서 bash /tmp/kiera-deploying.sh 로 이어가게 합니다.', 'Legacy KiERA used this as a global flag that switched deploy vendor-data/script responses into debug mode and stopped automatic progress. Current WIZ also drops only the script through write_files and skips automatic runcmd execution when deploying debug is enabled, so the preinstalled OS pauses and operators continue with bash /tmp/kiera-deploying.sh on site.'],
            ['노드 전원이 이미 켜져 있으면 PXE deploy 경로의 강제 power cycle을 생략합니다.', 'Skips the forced power cycle on the PXE deploy path when the node is already powered on.'],
            ['기존 KiERA는 node_ipmi chassis task에서 power cycle을 건너뛰는 DebugEnv 플래그였습니다. 현재 WIZ에서도 actions/relay deploy 경로가 이 값을 읽어 이미 켜진 노드의 power cycle을 우회합니다.', 'Legacy KiERA used this as a DebugEnv flag to skip the power cycle in the node_ipmi chassis task. Current WIZ also reads this value on the actions/relay deploy path to bypass the power cycle for nodes that are already on.'],
            ['서브넷 DHCP apply/regenerate 시 isc-dhcp-server 재시작 여부를 제어합니다.', 'Controls whether isc-dhcp-server is restarted during subnet DHCP apply/regenerate.'],
            ['기존 KiERA는 subnet signal에서 isc-dhcp-server 재시작 여부를 제어했습니다. 현재 WIZ도 DHCP config apply/regenerate 시 이 플래그를 읽어 서비스 재시작을 수행하거나 생략합니다.', 'Legacy KiERA controlled whether isc-dhcp-server was restarted from the subnet signal. Current WIZ also reads this flag during DHCP config apply/regenerate to perform or skip the service restart.'],
            ['NFS exports apply 시 exportfs -ra 실행 여부를 제어합니다.', 'Controls whether exportfs -ra is executed during NFS exports apply.'],
            ['기존 KiERA는 subnet signal과 RDMA 분기에서 exportfs/NFS 연동 여부를 제어했습니다. 현재 WIZ도 apply_nfs_exports 경로가 이 플래그를 읽어 exportfs 반영을 수행하거나 생략합니다.', 'Legacy KiERA controlled exportfs/NFS integration in subnet signal and RDMA branches. Current WIZ also reads this flag on the apply_nfs_exports path to apply or skip exportfs updates.'],
            ['서브넷 DHCP apply/regenerate 시 squid restart 시도 여부를 제어합니다.', 'Controls whether a squid restart is attempted during subnet DHCP apply/regenerate.'],
            ['기존 KiERA는 subnet signal에서 squid 재시작 여부를 제어했습니다. 현재 WIZ도 DHCP apply/regenerate 시 이 플래그가 켜져 있으면 squid 또는 squid.service 재시작을 시도합니다. 서비스가 없으면 작업 자체는 실패시키지 않습니다.', 'Legacy KiERA controlled whether squid was restarted from the subnet signal. Current WIZ also attempts to restart squid or squid.service during DHCP apply/regenerate when this flag is enabled, and it does not fail the operation when the service is absent.'],
        ];

        let text = String(value ?? '');
        for (const [source, target] of replacements) {
            text = text.split(source).join(target);
        }
        return text;
    }

    private localizeData(value: any): any {
        if (typeof value === 'string') return this.localizeText(value);
        if (Array.isArray(value)) return value.map((item) => this.localizeData(item));
        if (value && typeof value === 'object') {
            return Object.keys(value).reduce((result: any, key: string) => {
                result[key] = this.localizeData(value[key]);
                return result;
            }, {});
        }
        return value;
    }
}

export default PageKieraDebugComponent;
